/**
 * What the widget tells Hümo about the visitor besides their words, sent with
 * every message (backend services/visitor/visitorContext.ts):
 *   - from the browser: language, time zone, and how this visit reached the
 *     site (the referrer and UTM tags of its first page);
 *   - from the site, through `setContext`: the place the visitor chose on the
 *     site and the member signed in.
 * The backend keeps it only for businesses that turned it on, and works the
 * same without it (older widgets send none).
 */

/** What a site may tell Hümo about its visitor. Every field is optional. */
export interface SiteContext {
  /** The member signed in on the site; null or absent when nobody is. */
  user?: { id?: string | number; name?: string; email?: string; phone?: string } | null;
  /** The place the visitor chose on the site, e.g. its city/district filter. */
  location?: { city?: string; district?: string } | null;
}

export interface BrowserInfo {
  lang?: string;
  tz?: string;
  referrer?: string;
  utm?: Record<string, string>;
}

const VISIT_KEY = 'humo_visit';
const UTM_KEYS = ['source', 'medium', 'campaign', 'term', 'content'];

const short = (value: unknown, max: number): string | undefined => {
  if (typeof value !== 'string' && typeof value !== 'number') return undefined;
  const text = String(value).trim();
  return text ? text.slice(0, max) : undefined;
};

/** The page before the site, origin and path only; none when it is the site itself. */
function externalReferrer(): string | undefined {
  try {
    if (!document.referrer) return undefined;
    const ref = new URL(document.referrer);
    if (ref.origin === window.location.origin || !/^https?:$/.test(ref.protocol)) return undefined;
    return `${ref.origin}${ref.pathname}`.slice(0, 300);
  } catch {
    return undefined;
  }
}

function utmTags(search: string): Record<string, string> | undefined {
  const params = new URLSearchParams(search);
  const tags: Record<string, string> = {};
  for (const key of UTM_KEYS) {
    const value = short(params.get(`utm_${key}`), 100);
    if (value) tags[key] = value;
  }
  return Object.keys(tags).length ? tags : undefined;
}

/**
 * Where this visit came from, read on the first page the widget loads on and
 * kept for the tab (sessionStorage): a single-page site drops the UTM tags
 * from the address as soon as the visitor moves on.
 */
export function rememberVisit(): { referrer?: string; utm?: Record<string, string> } {
  try {
    const saved = window.sessionStorage.getItem(VISIT_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    // Storage blocked: read it from this page.
  }
  let visit: { referrer?: string; utm?: Record<string, string> } = {};
  try {
    visit = { referrer: externalReferrer(), utm: utmTags(window.location.search) };
    window.sessionStorage.setItem(VISIT_KEY, JSON.stringify(visit));
  } catch {
    // Nothing to remember.
  }
  return visit;
}

/** Language, time zone and this visit's source. */
export function browserInfo(): BrowserInfo | undefined {
  try {
    if (typeof window === 'undefined') return undefined;
    const info: BrowserInfo = {};
    const lang = short(navigator.language, 35);
    if (lang) info.lang = lang;
    const tz = short(Intl.DateTimeFormat().resolvedOptions().timeZone, 64);
    if (tz) info.tz = tz;
    const visit = rememberVisit();
    if (visit.referrer) info.referrer = visit.referrer;
    if (visit.utm) info.utm = visit.utm;
    return info;
  } catch {
    return undefined;
  }
}

/** Only the known fields, as short strings: the payload stays small and predictable. */
export function cleanContext(context: SiteContext | null | undefined): SiteContext {
  const out: SiteContext = {};
  const user = context?.user;
  if (user && typeof user === 'object') {
    const clean = {
      id: short(user.id, 64),
      name: short(user.name, 120),
      email: short(user.email, 200),
      phone: short(user.phone, 40),
    };
    const fields = Object.fromEntries(Object.entries(clean).filter(([, v]) => v !== undefined));
    if (Object.keys(fields).length) out.user = fields;
  }
  const location = context?.location;
  if (location && typeof location === 'object') {
    const fields = Object.fromEntries(
      Object.entries({ city: short(location.city, 60), district: short(location.district, 60) }).filter(([, v]) => v !== undefined),
    );
    if (Object.keys(fields).length) out.location = fields;
  }
  return out;
}
